import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Link2, Copy, Check, X, Loader2, Plus, MessageSquareQuote, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-gray-100 text-gray-600',
};

const CharityNotesView = () => {
  const [notes, setNotes] = useState([]);
  const [counts, setCounts] = useState({});
  const [invites, setInvites] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newInviteCampaign, setNewInviteCampaign] = useState('');
  const [compose, setCompose] = useState({ open: false, campaign_id: '', author_name: '', author_title: '', message: '' });

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const [n, i, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/charity-notes?status=${status}`, cfg),
        axios.get(`${BACKEND_URL}/api/charity-notes/invites`, cfg),
        axios.get(`${BACKEND_URL}/api/campaigns`, cfg).catch(() => ({ data: { campaigns: [] } })),
      ]);
      setNotes(n.data.notes || []);
      setCounts(n.data.counts || {});
      setInvites(i.data.invites || []);
      setCampaigns(c.data.campaigns || c.data || []);
    } catch (e) {
      console.error('Failed to load charity notes', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); }, [filter]);

  const createInvite = async () => {
    if (!newInviteCampaign) return toast.error('Pick a campaign first');
    setCreating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/charity-notes/invites`, { campaign_id: newInviteCampaign }, cfg);
      const link = `${window.location.origin}${res.data.link_path}`;
      navigator.clipboard.writeText(link);
      toast.success('Link created and copied — email it to the charity');
      setNewInviteCampaign('');
      load(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the link');
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (token) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/charity-notes/invites/${token}`, cfg);
      toast.success('Link revoked');
      load(filter);
    } catch (e) {
      toast.error('Could not revoke the link');
    }
  };

  const moderate = async (noteId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/charity-notes/${noteId}`, { status }, cfg);
      toast.success(status === 'approved' ? 'Note published to donor receipts' : 'Note hidden');
      load(filter);
    } catch (e) {
      toast.error('Could not update the note');
    }
  };

  const postOnBehalf = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/charity-notes`, compose, cfg);
      toast.success('Note published to donor receipts');
      setCompose({ open: false, campaign_id: '', author_name: '', author_title: '', message: '' });
      load(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not publish the note');
    }
  };

  const campaignName = (c) => c.name || c.title || c.campaign_id;

  return (
    <div className="space-y-6" data-testid="charity-notes-view">
      {/* Invite links */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Charity Thank-Yous</h3>
            <p className="text-sm text-gray-500 mt-1">
              Send a charity a private link so they can post a short note back. You approve it, then every donor on that campaign sees it on their receipt.
            </p>
          </div>
          <button
            onClick={() => setCompose({ ...compose, open: !compose.open })}
            className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
            data-testid="compose-note-toggle"
          >
            <Plus className="w-4 h-4" /> Post on their behalf
          </button>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={newInviteCampaign}
            onChange={(e) => setNewInviteCampaign(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm min-w-[260px] focus:outline-none focus:border-blue-500"
            data-testid="invite-campaign-select"
          >
            <option value="">Select a campaign…</option>
            {campaigns.map(c => (
              <option key={c.campaign_id} value={c.campaign_id}>{campaignName(c)} — {c.charity_name}</option>
            ))}
          </select>
          <button
            onClick={createInvite}
            disabled={creating}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"
            data-testid="create-invite-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            Create thank-you link
          </button>
        </div>

        {compose.open && (
          <div className="mt-5 border border-gray-200 rounded-xl p-5 space-y-3" data-testid="compose-note-form">
            <select value={compose.campaign_id} onChange={e => setCompose({ ...compose, campaign_id: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" data-testid="compose-campaign-select">
              <option value="">Campaign…</option>
              {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{campaignName(c)}</option>)}
            </select>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Author name" value={compose.author_name} onChange={e => setCompose({ ...compose, author_name: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm" data-testid="compose-author-input" />
              <input placeholder="Role (optional)" value={compose.author_title} onChange={e => setCompose({ ...compose, author_title: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm" data-testid="compose-role-input" />
            </div>
            <textarea rows={3} placeholder="Because of your team…" value={compose.message}
              onChange={e => setCompose({ ...compose, message: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-none" data-testid="compose-message-input" />
            <button onClick={postOnBehalf} className="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-5 py-2.5 rounded-lg text-sm" data-testid="compose-submit-btn">
              Publish note
            </button>
          </div>
        )}

        {invites.length > 0 && (
          <div className="mt-6 space-y-2">
            {invites.map(inv => {
              const link = `${window.location.origin}/thank-you/${inv.token}`;
              return (
                <div key={inv.token} className="flex flex-wrap items-center gap-3 border border-gray-200 rounded-xl px-4 py-3" data-testid={`invite-row-${inv.token}`}>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-semibold text-gray-900">{inv.charity_name}</p>
                    <p className="text-xs text-gray-500">{inv.campaign_title} · {inv.note_count} note{inv.note_count !== 1 ? 's' : ''}</p>
                  </div>
                  <code className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1 max-w-[280px] truncate">{link}</code>
                  <button onClick={() => { navigator.clipboard.writeText(link); toast.success('Link copied'); }}
                    className="p-2 text-gray-500 hover:text-blue-600" data-testid={`copy-invite-${inv.token}`}>
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={() => revokeInvite(inv.token)} className="p-2 text-gray-400 hover:text-red-600" data-testid={`revoke-invite-${inv.token}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Notes moderation */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-wrap gap-2 mb-5">
          {[{ v: 'all', l: 'All' }, { v: 'pending', l: 'Pending' }, { v: 'approved', l: 'Published' }, { v: 'rejected', l: 'Hidden' }].map(s => (
            <button key={s.v} onClick={() => setFilter(s.v)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === s.v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`notes-filter-${s.v}`}>
              {s.l}{s.v !== 'all' && counts[s.v] ? ` (${counts[s.v]})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-500">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="py-12 text-center text-gray-500" data-testid="charity-notes-empty">
            <MessageSquareQuote className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No thank-you notes yet. Create a link above and send it to a charity.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map(n => (
              <div key={n.note_id} className="border border-gray-200 rounded-xl p-5" data-testid={`charity-note-${n.note_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-3">
                      <Heart className="w-4 h-4 text-pink-500" />
                      <p className="font-bold text-gray-900">{n.charity_name}</p>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[n.status]}`}>
                        {n.status === 'approved' ? 'published' : n.status}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-gray-700 bg-gray-50 border-l-2 border-blue-500 pl-3 py-2 rounded-r italic">
                      &ldquo;{n.message}&rdquo;
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      {n.author_name}{n.author_title ? `, ${n.author_title}` : ''} · {new Date(n.created_at).toLocaleDateString()}
                      {n.source === 'posted_by_admin' ? ' · posted by your team' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {n.status !== 'approved' && (
                      <button onClick={() => moderate(n.note_id, 'approved')}
                        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-lg"
                        data-testid={`approve-note-${n.note_id}`}>
                        <Check className="w-3.5 h-3.5" /> Publish
                      </button>
                    )}
                    {n.status !== 'rejected' && (
                      <button onClick={() => moderate(n.note_id, 'rejected')}
                        className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg"
                        data-testid={`reject-note-${n.note_id}`}>
                        <X className="w-3.5 h-3.5" /> Hide
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CharityNotesView;
